import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { ChevronRight, Upload } from 'lucide-react';
import { Alert, AlertDescription } from '../ui/alert';

export default function SolicitudGeneralForm({ initialData, onSubmit }) {
  const [formData, setFormData] = useState({
    asunto: initialData?.asunto || '',
    descripcion: initialData?.descripcion || '',
    urgente: initialData?.urgente || false,
    observaciones: initialData?.observaciones || '',
  });
  const [errors, setErrors] = useState({});

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.asunto || formData.asunto.length < 5) {
      newErrors.asunto = 'El asunto debe tener al menos 5 caracteres';
    }
    if (!formData.descripcion || formData.descripcion.length < 20) {
      newErrors.descripcion = 'La descripción debe tener al menos 20 caracteres';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <Alert className="bg-slate-50 border-slate-200">
        <AlertDescription className="text-slate-800">
          Utiliza este formulario para solicitudes administrativas que no encajen en las categorías específicas.
          Nuestro equipo evaluará tu solicitud y te responderá a la brevedad.
        </AlertDescription>
      </Alert>

      <div className="space-y-4">
        <div>
          <Label>Asunto *</Label>
          <Input
            value={formData.asunto}
            onChange={(e) => handleChange('asunto', e.target.value)}
            placeholder="Resume brevemente tu solicitud"
            maxLength={200}
            className={errors.asunto ? 'border-red-500' : ''}
            data-testid="input-asunto"
          />
          {errors.asunto && <p className="text-red-500 text-xs mt-1">{errors.asunto}</p>}
        </div>

        <div>
          <Label>Descripción Detallada *</Label>
          <Textarea
            value={formData.descripcion}
            onChange={(e) => handleChange('descripcion', e.target.value)}
            placeholder="Explica con detalle qué necesitas. Incluye toda la información relevante para poder atenderte correctamente..."
            rows={6}
            maxLength={2000}
            className={errors.descripcion ? 'border-red-500' : ''}
            data-testid="input-descripcion"
          />
          {errors.descripcion && <p className="text-red-500 text-xs mt-1">{errors.descripcion}</p>}
          <p className="text-xs text-slate-500 mt-1">{formData.descripcion.length}/2000 caracteres</p>
        </div>

        <div className="flex items-center space-x-2 p-4 bg-red-50 rounded-lg border border-red-200">
          <Checkbox
            id="urgente"
            checked={formData.urgente}
            onCheckedChange={(checked) => handleChange('urgente', checked)}
          />
          <Label htmlFor="urgente" className="cursor-pointer text-red-900">
            <span className="font-medium">Marcar como urgente</span>
            <p className="text-sm text-red-700 mt-1">
              Solo marcar como urgente si requiere atención inmediata. Se asignará prioridad alta.
            </p>
          </Label>
        </div>

        <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center bg-slate-50">
          <Upload className="w-10 h-10 text-slate-400 mx-auto mb-3" />
          <p className="text-slate-600 mb-2">¿Necesitas adjuntar documentos?</p>
          <Button type="button" variant="outline" size="sm">
            Seleccionar archivos
          </Button>
          <p className="text-xs text-slate-500 mt-2">PDF, imágenes o documentos (máx 10MB)</p>
        </div>

        <div>
          <Label>Observaciones Adicionales</Label>
          <Textarea
            value={formData.observaciones}
            onChange={(e) => handleChange('observaciones', e.target.value)}
            placeholder="Cualquier información adicional que consideres relevante..."
            rows={2}
          />
        </div>
      </div>

      <Button type="submit" className="w-full" data-testid="btn-submit-form">
        Continuar <ChevronRight className="w-4 h-4 ml-2" />
      </Button>
    </form>
  );
}
